import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { UserApi } from "api/users/usersApi";
import { routes } from "consts";
import { ROLES_OPTIONS } from "consts/roles";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { PageMain, UserAvatar } from "components/molecules";
import { TanTable, TanTableColumn, useFilters, useInitFilters, usePagination, useSorting } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { useUsers } from "api/hooks/useUsers";

const FILTER_NAME = "users-filter";
export const StudentsPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);

  return (
    <StudentsPageInner
      initValues={{
        id: getInitFilterValue("id", "like"),
        email: getInitFilterValue("email", "like") ?? "",
        firstName: getInitFilterValue("firstName", "like") ?? "",
        surname: getInitFilterValue("surname", "like") ?? "",
        pesel: getInitFilterValue("pesel", "like") ?? "",
        phone: getInitFilterValue("phone", "like") ?? "",
        address: getInitFilterValue("address", "like") ?? "",
        bornDate: getInitFilterValue("bornDate", "like") ?? "",
        bornPlace: getInitFilterValue("bornPlace", "like") ?? "",
        roles: getInitFilterValues("roles"),
      }}
    />
  );
};
interface StudentsPageInnerProps {
  initValues: {
    id?: string;
    email: string;
    firstName: string;
    surname: string;
    pesel: string;
    phone: string;
    address: string;
    bornDate: string;
    bornPlace: string;
    roles: number[];
  };
}
const StudentsPageInner = ({ initValues }: StudentsPageInnerProps) => {
  const navigate = useNavigate();
  const { filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const { isFetching, isLoading, users, invalidate, total } = useUsers(skip, limit, filter, fieldWithOrder);

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, filter]);

  const columns = React.useMemo<TanTableColumn<UserApi>[]>(() => cols, []);

  return (
    <PageMain>
      <PageRichHeader header="Użytkownicy">
        <div className="flex grow flex-wrap justify-between gap-2">
          <div>
            <PaziaButton type="primary" title="Dodaj +" onClick={() => navigate(routes.USERS_ADD)} />
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <PaziaButton
              type="secondary"
              title="Zaproszenia do systemu"
              onClick={() => navigate(routes.STUDENTS_INVITED)}
            />
          </div>
        </div>
      </PageRichHeader>
      <TanTable
        data={users}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        initialFilters={[
          {
            id: COLUMN_KEYS.ID,
            value: initValues.id,
          },
          {
            id: COLUMN_KEYS.EMAIL,
            value: initValues.email,
          },
          {
            id: COLUMN_KEYS.FIRST_NAME,
            value: initValues.firstName,
          },
          {
            id: COLUMN_KEYS.SURNAME,
            value: initValues.surname,
          },
          {
            id: COLUMN_KEYS.PESEL,
            value: initValues.pesel,
          },
          {
            id: COLUMN_KEYS.PHONE,
            value: initValues.phone,
          },
          {
            id: COLUMN_KEYS.ADDRESS,
            value: initValues.address,
          },
          {
            id: COLUMN_KEYS.BORN_DATE,
            value: initValues.bornDate,
          },
          {
            id: COLUMN_KEYS.BORN_PLACE,
            value: initValues.bornPlace,
          },
          {
            id: COLUMN_KEYS.ROLES,
            value: initValues.roles,
          },
        ]}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        onRowClick={(x) => navigate(routes.STUDENT(x.id))}
        sorting={sorting}
        onEdit={(x) => navigate(routes.STUDENT_EDIT(x.id))}
      />
    </PageMain>
  );
};

const COLUMN_KEYS = {
  ID: "id",
  EMAIL: "email",
  FIRST_NAME: "firstName",
  SURNAME: "surname",
  PESEL: "pesel",
  PHONE: "phone",
  ADDRESS: "address",
  BORN_DATE: "bornDate",
  BORN_PLACE: "bornPlace",
  ROLES: "roles",
};
const cols: TanTableColumn<UserApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: true,
    size: 80,
    cell: (info) => info.getValue(),
    meta: {
      filterVariant: "id",
    },
  },
  {
    id: "avatarPhoto",
    hideOnMobile: true,
    accessorKey: "avatarPhoto",
    header: "Avatar",
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    cell: (info) => {
      const row = info.row.original;
      return <UserAvatar avatar={row.avatarPhoto} firstName={row.firstName} surname={row.surname} />;
    },
  },
  {
    id: "email",
    tdClassName: "max-md:break-all",
    accessorKey: "email",
    header: "Email",
    size: 75,
    enableColumnFilter: true,
  },
  {
    id: "firstName",
    accessorKey: "firstName",
    header: "Imię",
    size: 75,
    enableColumnFilter: true,
  },
  {
    id: "surname",
    accessorKey: "surname",
    header: "Nazwisko",
    size: 75,
    enableColumnFilter: true,
  },
  {
    id: "roles",
    hideOnMobile: true,
    accessorKey: "roles",
    header: "Rola",
    enableColumnFilter: true,
    enableSorting: false,
    size: 100,
    cell: (info) => {
      const roles = info.getValue<UserApi["roles"]>() ?? [];
      return roles
        .map((role) => ROLES_OPTIONS.find((option) => option.value === role.id.toString())?.label ?? role.name)
        .join(", ");
    },
    meta: {
      filterVariant: "select",
      options: ROLES_OPTIONS,
    },
  },
  {
    id: "pesel",
    hideOnMobile: true,
    accessorKey: "pesel",
    header: "PESEL",
    enableColumnFilter: true,
    enableSorting: false,
  },
  {
    id: "phone",
    hideOnMobile: true,
    accessorKey: "phone",
    header: "Telefon",
    size: 80,
    enableColumnFilter: true,
    enableSorting: false,
  },
  {
    id: "address",
    hideOnMobile: true,
    accessorKey: "address",
    header: "Adres",
    size: 150,
    enableColumnFilter: false, // TODO enable
    enableSorting: false,
  },
  {
    id: "bornDate",
    hideOnMobile: true,
    accessorKey: "bornDate",
    header: "Data urodzenia",
    size: 120,
    enableColumnFilter: false, // TODO enable
    cell: (info) => {
      const date = info.getValue<Date>();
      return date ? date.toLocaleDateString("pl-PL") : "";
    },
  },
  {
    id: "bornPlace",
    hideOnMobile: true,
    accessorKey: "bornPlace",
    header: "Miejsce urodzenia",
    size: 150,
    enableColumnFilter: false, // TODO enable
    enableSorting: false,
  },
  {
    id: "createdDate",
    hideOnMobile: true,
    accessorKey: "createdDate",
    header: "Dołączył(a)",
    cell: (info) => info.getValue<Date>().toLocaleString(),
    // @ts-ignore todo
    size: "auto",
    enableColumnFilter: false, // TODO enable
  },
];
